/**
 * StatusStrips — backend runtime info, auth session, and persistent
 * slice cards (workspace, drawings, audit).
 * Extracted from App.tsx — pure structural refactor.
 */

import {
  selectCurrentUser,
  useEditorStore,
} from "../state/editorStore";
import { useT } from "../i18n";
import type { BackendRuntimeState } from "./EditorHeader";
import type {
  AdminAuditResponse,
  DrawingsResponse,
  WorkspacesResponse,
} from "../backend/backendApi";
import {
  buildLocalWorkspaceFallback,
  buildLocalDrawingFallback,
  buildLocalAuditFallback,
} from "../helpers/backendHelpers";

export type BackendPersistentSliceState = {
  status: "loading" | "ready" | "partial" | "error";
  workspacesResponse: WorkspacesResponse | null;
  drawingsResponse: DrawingsResponse | null;
  auditResponse: AdminAuditResponse | null;
  errorMessage?: string;
};

export interface StatusStripsProps {
  backendRuntime: BackendRuntimeState;
  backendPersistentSlice: BackendPersistentSliceState;
}

export function StatusStrips({
  backendRuntime,
  backendPersistentSlice,
}: StatusStripsProps) {
  const t = useT();
  const state = useEditorStore((store) => store.state);
  const currentUser = selectCurrentUser(state);

  const backendWorkspace =
    backendPersistentSlice.workspacesResponse?.data[0] ?? null;
  const backendDrawings = backendPersistentSlice.drawingsResponse?.data ?? [];
  const backendAuditEvents = backendPersistentSlice.auditResponse?.data ?? [];
  const localWorkspaceFallback = buildLocalWorkspaceFallback(state, currentUser);
  const localDrawingFallback = buildLocalDrawingFallback(state, currentUser);
  const localAuditFallback = buildLocalAuditFallback(state);
  const isAdminLikeUser =
    currentUser.role === "admin" || currentUser.role === "absolute_admin";
  const hasPersistentBackendData = Boolean(
    backendPersistentSlice.workspacesResponse ||
    backendPersistentSlice.drawingsResponse ||
    backendPersistentSlice.auditResponse,
  );
  const workspaceCard =
    backendWorkspace ??
    (backendPersistentSlice.workspacesResponse
      ? {
          ...localWorkspaceFallback,
          name: t("backend.no_persistent_workspace"),
        }
      : localWorkspaceFallback);
  const drawingCards = backendPersistentSlice.drawingsResponse
    ? backendDrawings
    : [localDrawingFallback];
  const auditCards = backendPersistentSlice.auditResponse
    ? backendAuditEvents
    : localAuditFallback;
  const persistentSourceLabel =
    backendPersistentSlice.status === "loading"
      ? t("backend.source.loading")
      : !backendRuntime.authenticated
        ? t("backend.source.guest")
        : !isAdminLikeUser && !hasPersistentBackendData
          ? t("backend.source.auth")
          : !isAdminLikeUser && backendPersistentSlice.auditResponse === null
            ? t("backend.source.backend_audit_gated")
            : backendPersistentSlice.status === "ready"
              ? t("backend.source.backend")
              : backendPersistentSlice.status === "partial"
                ? t("backend.source.mixed")
                : t("backend.source.fallback");

  return (
    <>
      <section className="status-strip" data-testid="runtime-status-strip">
        <article className="status-card" data-testid="backend-runtime-card">
          <span className="status-label">{t("status.backend_runtime")}</span>
          <strong>
            {backendRuntime.status === "ready"
              ? t("status.ready_via", {
                  authMode: backendRuntime.authMode ?? "",
                  url: backendRuntime.apiBaseUrl,
                })
              : backendRuntime.status === "error"
                ? t("status.fallback_mode", {
                    error: backendRuntime.errorMessage ?? "",
                  })
                : t("status.connecting", { url: backendRuntime.apiBaseUrl })}
          </strong>
        </article>
        <article className="status-card" data-testid="mutation-path-card">
          <span className="status-label">{t("status.mutation_path")}</span>
          <strong>{t("status.mutation_path_value")}</strong>
        </article>
        <article className="status-card" data-testid="current-scope-card">
          <span className="status-label">{t("status.current_scope")}</span>
          <strong>
            {backendRuntime.status === "ready"
              ? backendRuntime.notice
              : t("status.local_shell_fallback")}
          </strong>
        </article>
      </section>

      <section className="status-strip" data-testid="session-status-strip">
        <article className="status-card" data-testid="auth-session-card">
          <span className="status-label">{t("status.auth_session")}</span>
          <strong>
            {backendRuntime.status === "loading"
              ? backendRuntime.sessionDescription
              : backendRuntime.status === "error"
                ? backendRuntime.sessionDescription
                : `${backendRuntime.sessionLabel} / ${backendRuntime.sessionDescription}`}
          </strong>
          {backendRuntime.status === "ready" &&
          !backendRuntime.authenticated ? (
            <span className="microcopy">
              {t("status.google_signin_disabled")}
            </span>
          ) : null}
          {backendRuntime.status === "error" ? (
            <span className="microcopy">{t("status.auth_fallback")}</span>
          ) : null}
        </article>
      </section>

      <section className="surface-card" data-testid="persistent-slice-card">
        <div className="section-heading">
          <div>
            <p className="section-label">{t("backend.slice_title")}</p>
            <h2>{t("backend.slice_subtitle")}</h2>
          </div>
          <span
            className={`badge ${backendPersistentSlice.status === "ready" ? "pill-ok" : "pill-warn"}`}
            data-testid="persistent-source-badge"
          >
            {persistentSourceLabel}
          </span>
        </div>

        <p className="panel-note">
          {backendPersistentSlice.status === "loading"
            ? t("backend.loading_slices", { url: backendRuntime.apiBaseUrl })
            : backendRuntime.status === "ready"
              ? backendRuntime.authenticated
                ? hasPersistentBackendData
                  ? isAdminLikeUser
                    ? t("backend.loaded_admin", {
                        url: backendRuntime.apiBaseUrl,
                      })
                    : t("backend.loaded_user", {
                        url: backendRuntime.apiBaseUrl,
                      })
                  : t("backend.protected_unloaded")
                : t("backend.guest_fallback")
              : backendPersistentSlice.status === "partial"
                ? t("backend.partial_load", {
                    error: backendPersistentSlice.errorMessage ?? "",
                  })
                : t("backend.unavailable", {
                    error:
                      backendPersistentSlice.errorMessage ??
                      t("backend.preview_fallback"),
                  })}
        </p>

        <div className="panel-stack">
          <article className="section-subcard" data-testid="workspace-card">
            <div className="section-heading">
              <div>
                <p className="section-label">{t("backend.workspace")}</p>
                <h3>{workspaceCard.name}</h3>
              </div>
              <span className="badge">
                {backendPersistentSlice.workspacesResponse
                  ? t("backend.tag_backend")
                  : t("backend.tag_local_fallback")}
              </span>
            </div>
            <p className="panel-note">
              {backendPersistentSlice.workspacesResponse
                ? t("backend.workspace_intro")
                : t("backend.workspace_fallback")}
            </p>
            <ul className="flat-list">
              <li>{t("backend.slug", { slug: workspaceCard.slug })}</li>
              <li>{t("backend.owner", { owner: workspaceCard.ownerEmail })}</li>
              <li>
                {t("backend.members", { count: workspaceCard.memberCount })}
              </li>
              <li>
                {t("backend.visibility", {
                  visibility: workspaceCard.visibility,
                })}
              </li>
              <li>
                {t("backend.deployment", {
                  mode: workspaceCard.deploymentMode,
                })}
              </li>
            </ul>
          </article>

          <article className="section-subcard" data-testid="drawings-card">
            <div className="section-heading">
              <div>
                <p className="section-label">{t("backend.drawings")}</p>
                <h3>
                  {backendDrawings.length > 0
                    ? t("backend.records", { count: backendDrawings.length })
                    : t("backend.local_fallback")}
                </h3>
              </div>
              <span className="badge">
                {backendPersistentSlice.drawingsResponse
                  ? t("backend.tag_backend")
                  : t("backend.tag_local_fallback")}
              </span>
            </div>
            <div className="history-list">
              {drawingCards.map((drawing) => (
                <article
                  key={`${drawing.slug}-${drawing.id}`}
                  className="history-card"
                >
                  <strong>{drawing.name}</strong>
                  <span>
                    {drawing.slug} /{" "}
                    {drawing.workspaceSlug || workspaceCard.slug}
                  </span>
                  <p>
                    {drawing.sourceFormat} / {drawing.status}
                  </p>
                </article>
              ))}
            </div>
          </article>

          <article className="section-subcard" data-testid="audit-card">
            <div className="section-heading">
              <div>
                <p className="section-label">{t("backend.audit")}</p>
                <h3>
                  {backendAuditEvents.length > 0
                    ? t("backend.records", {
                        count: backendAuditEvents.length,
                      })
                    : t("backend.local_fallback")}
                </h3>
              </div>
              <span className="badge">
                {backendPersistentSlice.auditResponse
                  ? t("backend.tag_backend")
                  : t("backend.tag_local_fallback")}
              </span>
            </div>
            <div className="history-list">
              {auditCards.map((event, index) => (
                <article key={`${event.id ?? index}`} className="history-card">
                  <strong>{event.command}</strong>
                  <span>
                    {event.commandSource} /{" "}
                    {event.alias || event.workspaceSlug || "persistent"}
                  </span>
                  <p>
                    {event.afterSummary || event.command} / {event.drawingSlug}
                  </p>
                </article>
              ))}
            </div>
          </article>
        </div>
      </section>
    </>
  );
}
